import * as THREE from 'three';
import {
  LUNAR_ASSET_COLORS,
  lunarCraterProfile,
  lunarCraterSurfaceShade,
} from '../assets/LunarAssetKit';
import type { SavedMoonDesign } from '../game/MoonDesign';

const SOURCE_MOON_RADIUS = 2.45;
const DISPLAY_RADIUS = 0.46;

export class OrbitMoon {
  readonly group = new THREE.Group();
  private readonly geometry: THREE.SphereGeometry;
  private readonly texture: THREE.CanvasTexture;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly mesh: THREE.Mesh;

  constructor(readonly design: SavedMoonDesign, detail = 1) {
    const widthSegments = detail > 0 ? 48 : 28;
    const heightSegments = detail > 0 ? 32 : 20;
    this.geometry = new THREE.SphereGeometry(DISPLAY_RADIUS, widthSegments, heightSegments);
    this.deformGeometry();
    this.texture = this.createTexture();
    this.material = new THREE.MeshStandardMaterial({
      color: LUNAR_ASSET_COLORS.surface,
      map: this.texture,
      bumpMap: this.texture,
      bumpScale: 0.009,
      roughness: 0.98,
      metalness: 0,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.userData.moonId = design.id;
    this.group.userData.moonId = design.id;
    this.group.add(this.mesh);
  }

  setSelected(selected: boolean): void {
    this.material.emissive.set(selected ? '#61757c' : '#000000');
    this.material.emissiveIntensity = selected ? 0.18 : 0;
  }

  update(delta: number, autoRotate = true): void {
    if (autoRotate) this.mesh.rotation.y += delta * 0.16;
  }

  dispose(): void {
    this.geometry.dispose();
    this.texture.dispose();
    this.material.dispose();
  }

  private deformGeometry(): void {
    const position = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    const normal = new THREE.Vector3();
    const displayScale = DISPLAY_RADIUS / SOURCE_MOON_RADIUS;
    for (let index = 0; index < position.count; index += 1) {
      normal.fromBufferAttribute(position, index).normalize();
      let radialOffset = 0;
      for (const crater of this.design.craters) {
        const dot = THREE.MathUtils.clamp(
          normal.x * crater.normal.x + normal.y * crater.normal.y + normal.z * crater.normal.z,
          -1,
          1,
        );
        const distance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * SOURCE_MOON_RADIUS;
        if (distance >= crater.radius) continue;
        radialOffset += lunarCraterProfile(distance / crater.radius, crater.depth);
      }
      const radius = DISPLAY_RADIUS + Math.max(radialOffset, -0.28) * displayScale;
      position.setXYZ(index, normal.x * radius, normal.y * radius, normal.z * radius);
    }
    position.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingSphere();
  }

  private createTexture(): THREE.CanvasTexture {
    const width = 512;
    const height = 256;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not create orbit moon texture.');
    const image = context.createImageData(width, height);
    const seed = this.design.surfaceSeed || 1;
    const hash = (x: number, y: number): number => {
      let value = Math.imul(x + seed, 374761393) + Math.imul(y + seed * 3, 668265263);
      value = Math.imul(value ^ (value >>> 13), 1274126177);
      return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
    };
    const mareFrames = this.design.craters.filter((crater) => crater.hasMare).map((crater) => {
      const normal = new THREE.Vector3(crater.normal.x, crater.normal.y, crater.normal.z).normalize();
      const reference = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
      const tangentX = reference.cross(normal).normalize();
      const tangentY = new THREE.Vector3().crossVectors(normal, tangentX).normalize();
      return { crater, normal, tangentX, tangentY };
    });

    for (let y = 0; y < height; y += 1) {
      const phi = (y / (height - 1)) * Math.PI;
      const sinPhi = Math.sin(phi);
      const ny = Math.cos(phi);
      for (let x = 0; x < width; x += 1) {
        const theta = (x / width) * Math.PI * 2;
        const nx = -Math.cos(theta) * sinPhi;
        const nz = Math.sin(theta) * sinPhi;
        const noise = (hash(x >> 2, y >> 2) - 0.5) * 22 + (hash(x, y) - 0.5) * 7;
        let red = 184 + noise + 2;
        let green = 184 + noise + 1;
        let blue = 184 + noise - 2;

        for (const crater of this.design.craters) {
          const dot = THREE.MathUtils.clamp(
            nx * crater.normal.x + ny * crater.normal.y + nz * crater.normal.z,
            -1,
            1,
          );
          const distance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * SOURCE_MOON_RADIUS;
          if (distance >= crater.radius) continue;
          const shade = lunarCraterSurfaceShade(distance / crater.radius);
          red *= shade;
          green *= shade * 0.975;
          blue *= shade * 0.93;
        }

        for (const { crater, normal, tangentX, tangentY } of mareFrames) {
          const dot = THREE.MathUtils.clamp(nx * normal.x + ny * normal.y + nz * normal.z, -1, 1);
          const distance = Math.sqrt(Math.max(0, 2 - 2 * dot)) * SOURCE_MOON_RADIUS;
          const localX = nx * tangentX.x + ny * tangentX.y + nz * tangentX.z;
          const localY = nx * tangentY.x + ny * tangentY.y + nz * tangentY.z;
          const angle = Math.atan2(localY, localX);
          const coast =
            Math.sin(angle * 3 + crater.id * 0.83) * 0.085
            + Math.sin(angle * 5 - crater.id * 0.47) * 0.045
            + Math.sin(angle * 9 + crater.id * 1.19) * 0.022;
          const radius = crater.radius * 1.86 * crater.mareFillScale * (1 + coast);
          if (distance >= radius) continue;
          const coverage = 1 - THREE.MathUtils.smoothstep(distance / radius, 0.7, 1);
          const mottling = Math.sin(nx * 43 + ny * 61 + nz * 37 + crater.id) * 4;
          const opacity = coverage * 0.86;
          red = THREE.MathUtils.lerp(red, 94 + mottling, opacity);
          green = THREE.MathUtils.lerp(green, 95 + mottling * 0.92, opacity);
          blue = THREE.MathUtils.lerp(blue, 92 + mottling * 0.78, opacity);
        }

        const offset = (y * width + x) * 4;
        image.data[offset] = Math.round(THREE.MathUtils.clamp(red, 36, 220));
        image.data[offset + 1] = Math.round(THREE.MathUtils.clamp(green, 36, 220));
        image.data[offset + 2] = Math.round(THREE.MathUtils.clamp(blue, 34, 216));
        image.data[offset + 3] = 255;
      }
    }
    context.putImageData(image, 0, 0);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    return texture;
  }
}
